import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

class TseSubscriptionBuybox extends HTMLElement {
  /** @type {Element | null | undefined} */
  closestSection;

  connectedCallback() {
    this.closestSection = this.closest('.shopify-section, dialog');
    this.closestSection?.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    this.addEventListener('change', this.handleDeliveryCycleChange);
    this.applySellingPlanToForm();
  }

  disconnectedCallback() {
    this.closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    this.removeEventListener('change', this.handleDeliveryCycleChange);
  }

  /**
   * @param {Event} event
   */
  handleDeliveryCycleChange = (event) => {
    const select = event.target;

    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-delivery-cycle')) {
      return;
    }

    this.dataset.sellingPlanId = select.value;
    this.dataset.hasSellingPlan = 'true';
    this.applySellingPlanToForm();
  };

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const newBuybox = event.detail.data.html.querySelector('tse-subscription-buybox');

    if (newBuybox) {
      morph(this, newBuybox);
      this.syncRootState(newBuybox);
    }

    this.syncSellingPlanDataset();
    this.applySellingPlanToForm();
    this.syncOneTimeOnly(event.detail.data.html);
  };

  /**
   * Mirrors the buy buttons one-time-only modifier so subscription-only copy hides for variants without selling plans.
   * @param {Document} html
   */
  syncOneTimeOnly(html) {
    const newBuyButtons = html.querySelector('.buy-buttons-block');
    if (!newBuyButtons || !this.closestSection) return;

    const isOneTimeOnly = newBuyButtons.classList.contains('buy-buttons-block--one-time-only');

    for (const buyButtons of this.closestSection.querySelectorAll('.buy-buttons-block')) {
      buyButtons.classList.toggle('buy-buttons-block--one-time-only', isOneTimeOnly);
    }
  }

  /**
   * `morph` runs children-only, so the host class and plan flags keep the previous
   * variant's values. Without this a variant loaded as OTP-only stays `display: none`
   * after switching to a variant that does have Purchase Options.
   *
   * @param {Element} newBuybox
   */
  syncRootState(newBuybox) {
    this.className = newBuybox.className;

    const hasPlan = newBuybox.getAttribute('data-has-selling-plan') === 'true';
    const planId = newBuybox.getAttribute('data-selling-plan-id') ?? '';

    this.dataset.hasSellingPlan = hasPlan ? 'true' : 'false';

    if (hasPlan && planId) {
      this.dataset.sellingPlanId = planId;
    } else {
      delete this.dataset.sellingPlanId;
    }
  }

  /**
   * Keeps the plan flags aligned with the markup currently in the DOM.
   */
  syncSellingPlanDataset() {
    const cycleSelect = this.querySelector('[data-delivery-cycle]');
    const selectPlan =
      cycleSelect instanceof HTMLSelectElement && cycleSelect.value ? cycleSelect.value : '';

    if (selectPlan) {
      this.dataset.hasSellingPlan = 'true';
      this.dataset.sellingPlanId = selectPlan;
      return;
    }

    delete this.dataset.sellingPlanId;
    this.dataset.hasSellingPlan = 'false';
  }

  /**
   * Stamp a selling plan only when this variant actually has Purchase Options.
   * Otherwise remove any leftover selling_plan so OTP Add to cart works.
   */
  applySellingPlanToForm() {
    const productId = this.dataset.productId;
    const planId = this.dataset.hasSellingPlan === 'true' ? this.dataset.sellingPlanId || '' : '';
    const form = this.closestSection?.querySelector(
      `product-form-component[data-product-id="${productId}"] form:not([data-buy-once-form])`
    );

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    let input = form.querySelector('input[name="selling_plan"]');

    if (!planId) {
      input?.remove();
      delete this.dataset.sellingPlanId;
      return;
    }

    this.dataset.sellingPlanId = planId;

    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      form.appendChild(input);
    }

    input.value = planId;
  }
}

if (!customElements.get('tse-subscription-buybox')) {
  customElements.define('tse-subscription-buybox', TseSubscriptionBuybox);
}
